// ──────────────────────────────────────────────────────────────────────────────
// Julat Harga — API Service Layer
// All calls go to backend: /julatHarga/*
// Backend uses ClickHouse for blk_iii (14M+ rows) + MySQL for lookups.
// ──────────────────────────────────────────────────────────────────────────────

import axios from "../../plugins/axios";

const API = process.env.VUE_APP_API_URL;

/**
 * Fetch main table (aggregated items) with server-side pagination.
 */
export async function fetchMainTable({ page, itemsPerPage, negeri, pusingan, dp, search, sortBy, sortDesc }) {
  const res = await axios.get(`${API}/julatHarga/getItems`, {
    params: {
      page: page || 1,
      itemsPerPage: itemsPerPage || 10,
      negeri: negeri || '',
      pusingan: pusingan || '',
      dp: dp || '',
      search: search || '',
      sortBy: (sortBy && sortBy.length) ? sortBy[0] : '',
      sortDesc: (sortDesc && sortDesc.length) ? sortDesc[0] : false,
    },
  });
  return res.data;
}

/**
 * Fetch drill-down detail records with server-side pagination.
 */
export async function fetchDrillTable({ kod, page, itemsPerPage, negeri, pusingan, dp, minH, maxH, minQ, maxQ, hid, outlierType, sortBy, sortDesc }) {
  const res = await axios.get(`${API}/julatHarga/getDetails`, {
    params: {
      kod,
      page: page || 1,
      itemsPerPage: itemsPerPage || 10,
      negeri: negeri || '',
      pusingan: pusingan || '',
      dp: dp || '',
      minH: (minH != null && minH !== '') ? minH : '',
      maxH: (maxH != null && maxH !== '') ? maxH : '',
      minQ: (minQ != null && minQ !== '') ? minQ : '',
      maxQ: (maxQ != null && maxQ !== '') ? maxQ : '',
      hid: (hid != null && hid !== '') ? hid : '',
      outlierType: (outlierType != null && outlierType !== '') ? outlierType : '',
      sortBy: (sortBy && sortBy.length) ? sortBy[0] : '',
      sortDesc: (sortDesc && sortDesc.length) ? sortDesc[0] : false,
    },
  });
  return res.data;
}

/**
 * Fetch ALL main table items for Excel export.
 */
export async function fetchMainTableExport({ negeri, pusingan, dp, search }) {
  const res = await axios.get(`${API}/julatHarga/exportItems`, {
    params: {
      negeri: negeri || '',
      pusingan: pusingan || '',
      dp: dp || '',
      search: search || '',
    },
  });
  return res.data.items;
}

/**
 * Download ALL drill detail records as CSV.
 *
 * The backend streams ready-made CSV rather than returning JSON: the biggest item
 * (11100009) is 522k records, which was a 257 MB JSON payload the browser could not
 * parse without running out of memory. Returns a Blob for the caller to save — no
 * JSON.parse, no client-side CSV assembly.
 */
export async function fetchDrillTableExport({ kod, minH, maxH, minQ, maxQ, hid, outlierType, negeri, pusingan, dp }) {
  const res = await axios.get(`${API}/julatHarga/exportDetails`, {
    responseType: 'blob',
    params: {
      kod,
      negeri: negeri || '',
      pusingan: pusingan || '',
      dp: dp || '',
      minH: (minH != null && minH !== '') ? minH : '',
      maxH: (maxH != null && maxH !== '') ? maxH : '',
      minQ: (minQ != null && minQ !== '') ? minQ : '',
      maxQ: (maxQ != null && maxQ !== '') ? maxQ : '',
      hid: (hid != null && hid !== '') ? hid : '',
      outlierType: (outlierType != null && outlierType !== '') ? outlierType : '',
    },
  });
  return res.data;
}

/**
 * Fetch multi-method consensus anomaly analysis for a single KodItem.
 * Backend computes 3 methods (Unit Price IQR, Regression, Peer Group) over the
 * FULL dataset and returns per-record flags + consensus score.
 */
export async function fetchConsensus({ kod, negeri, pusingan, dp }) {
  const res = await axios.get(`${API}/julatHarga/getConsensus`, {
    params: {
      kod,
      negeri: negeri || '',
      pusingan: pusingan || '',
      dp: dp || '',
    },
  });
  return res.data;
}

/**
 * Fetch ONE server-paginated page of consensus records for a KodItem, with the
 * table filters + sort applied in ClickHouse. Scales to items with hundreds of
 * thousands of records (the summary getConsensus call only returns a sample).
 */
export async function fetchConsensusRecords({ kod, negeri, pusingan, dp, page, itemsPerPage, sortBy, sortDesc, conf, method, direction, search }) {
  const res = await axios.get(`${API}/julatHarga/getConsensusRecords`, {
    params: {
      kod,
      negeri: negeri || '',
      pusingan: pusingan || '',
      dp: dp || '',
      page: page || 1,
      itemsPerPage: itemsPerPage || 25,
      sortBy: sortBy || '',
      sortDesc: sortDesc == null ? true : sortDesc,
      conf: conf || '',
      method: method || '',
      direction: direction || '',
      search: search || '',
    },
  });
  return res.data;
}

/**
 * Fetch filter dropdown options.
 */
export async function getFilterOptions() {
  const res = await axios.get(`${API}/julatHarga/getFilterOptions`);
  return res.data;
}

/**
 * IQR calculation helper (used by chart in frontend).
 */
export function calcStats(arr) {
  if (!arr || !arr.length) return { min: 0, max: 0, q1: 0, median: 0, q3: 0, iqr: 0, lower: 0, upper: 0, outliers: [], wL: 0, wH: 0 };
  const s = [...arr].sort((a, b) => a - b);
  const n = s.length;
  const q1 = s[Math.floor(n * 0.25)];
  const q3 = s[Math.floor(n * 0.75)];
  const median = n % 2 === 0 ? (s[n / 2 - 1] + s[n / 2]) / 2 : s[Math.floor(n / 2)];
  const iqr = q3 - q1;
  const lower = q1 - 1.5 * iqr;
  const upper = q3 + 1.5 * iqr;
  const outliers = arr.filter(v => v < lower || v > upper);
  const wL = s.find(v => v >= lower) || s[0];
  const wH = [...s].reverse().find(v => v <= upper) || s[n - 1];
  return { min: s[0], max: s[n - 1], q1, median, q3, iqr, lower, upper, outliers, wL, wH };
}
